import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_COMPANY, LIST_SCENARIOS, COUNT_LOGIN_LOGS_BY_USER, COUNT_EVALUATIONS_BY_USER, COUNT_EVALUATIONS_BY_MONTH } from '../../graphql/queries';
import { Scenario } from '../../types/models';
import { RootState } from '../../store';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  useTheme,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  Settings as SettingsIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';

import scenariosIcon from '../../assets/images/ic_scenarios.svg';

// ページあたりのシナリオ表示件数
const SCENARIOS_PER_PAGE = 12;

const ScenarioListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [userScenarioCounts, setUserScenarioCounts] = useState<Record<string, number>>({});
  // すでにリクエストを送信したシナリオを追跡するためのref
  const fetchedScenariosRef = useRef<Set<string>>(new Set());
  
  // 表示中のシナリオリストとnextTokenの状態管理
  const [displayedScenarios, setDisplayedScenarios] = useState<Scenario[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // タグフィルター用の状態管理
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // 初回シナリオ一覧を取得
  const { loading, error, data } = useQuery(LIST_SCENARIOS, {
    variables: {
      companyId: currentUser?.companyId,
      limit: SCENARIOS_PER_PAGE
    },
    skip: !currentUser?.companyId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.listScenarios) {
        const scenarios = (data.listScenarios.items || [])
          .filter((scenario: Scenario | null) => scenario !== null && scenario !== undefined)
          .filter((scenario: Scenario) => scenario.isActive);

        setDisplayedScenarios(scenarios);
        setNextToken(data.listScenarios.nextToken || null);
      }
    }
  });

  // 追加のシナリオを取得するためのlazyQuery
  const [loadMoreScenarios] = useLazyQuery(LIST_SCENARIOS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.listScenarios) {
        const newScenarios = (data.listScenarios.items || [])
          .filter((scenario: Scenario | null) => scenario !== null && scenario !== undefined)
          .filter((scenario: Scenario) => scenario.isActive);

        setDisplayedScenarios(prev => {
          const existingIds = new Set(prev.map(s => s.scenarioId));
          const uniqueNewScenarios = newScenarios.filter((scenario: Scenario) => !existingIds.has(scenario.scenarioId));
          return [...prev, ...uniqueNewScenarios];
        });
        setNextToken(data.listScenarios.nextToken || null);
      }
      setLoadingMore(false);
    },
    onError: (error) => {
      console.error('追加シナリオの取得エラー:', error);
      setLoadingMore(false);
    }
  });

  // ユーザー名を生成（${companyId}#{givenUserId}形式）
  const username = currentUser?.givenUserId ? `${currentUser.companyId}#${currentUser.givenUserId}` : null;
  
  // ユーザーのシナリオ実施回数を取得するためのクエリ
  const [fetchUserScenarioCount] = useLazyQuery(COUNT_EVALUATIONS_BY_USER);

  // シナリオごとのユーザー実施回数を取得する関数
  const fetchScenarioCount = useCallback(async (scenarioId: string) => {
    if (!username) return;
    
    try {
      const { data } = await fetchUserScenarioCount({
        variables: {
          userId: username,
          scenarioId
        }
      });
      
      if (data && data.countEvaluationsByUser !== undefined) {
        setUserScenarioCounts(prev => ({
          ...prev,
          [scenarioId]: data.countEvaluationsByUser
        }));
      }
    } catch (error) {
      console.error(`Error fetching count for scenario ${scenarioId}:`, error);
    }
  }, [username, fetchUserScenarioCount]);
  
  // デバッグ用：ユーザー情報とユーザー名を出力
  // useEffect(() => {
  //   if (currentUser) {
  //     console.log('現在のユーザー情報:', currentUser);
  //     console.log('生成されたユーザー名:', username);
  //   }
  // }, [currentUser, username]);
  
  // 今月の開始日と終了日を計算
  const getMonthDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // 今月の初日
    const startDate = new Date(year, month, 1);
    // 来月の初日 - 1日 = 今月の最終日
    const endDate = new Date(year, month + 1, 0);
    
    // ISO形式の文字列に変換
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };
  
  const { startDate, endDate } = getMonthDateRange();
  
  // 今月のログイン回数を取得
  const { loading: loadingLogs, error: errorLogs, data: loginCountData } = useQuery(COUNT_LOGIN_LOGS_BY_USER, {
    variables: {
      userId: username,
      startDate,
      endDate
    },
    skip: !username,
    fetchPolicy: 'network-only'
  });
  // ログイン回数をコンソールに出力（デバッグ用）
  // useEffect(() => {
  //   if (loginCountData !== undefined) {
  //     console.log('今月のログイン回数:', loginCountData.countLoginLogsByUser);
  //   }
  // }, [loginCountData]);

  // 今月のログイン回数
  const monthlyLoginCount = loginCountData?.countLoginLogsByUser || 0;

  // すべてのシナリオから利用可能なタグを抽出（重複を除く）
  const availableTags = React.useMemo(() => {
    const tagsSet = new Set<string>();
    displayedScenarios.forEach(scenario => {
      if (scenario.tags) {
        scenario.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    return Array.from(tagsSet).sort();
  }, [displayedScenarios]);

  // タグでフィルタリングされたシナリオ
  const filteredScenarios = React.useMemo(() => {
    if (selectedTags.length === 0) {
      return displayedScenarios;
    }
    return displayedScenarios.filter(scenario => {
      if (!scenario.tags || scenario.tags.length === 0) {
        return false;
      }
      // 選択されたすべてのタグがシナリオに含まれている場合のみ表示
      return selectedTags.every(selectedTag => 
        scenario.tags!.includes(selectedTag)
      );
    });
  }, [displayedScenarios, selectedTags]);

  // 表示中のシナリオをソート（元の配列を変更しないように新しい配列を作成）
  const sortedScenarios = [...filteredScenarios].sort((a: Scenario, b: Scenario) => {
    // 1. 所属（department）でソート：undefined/空文字列が最上位、以降は辞書順
    const deptA = a.department || '';
    const deptB = b.department || '';
    
    if (deptA === '' && deptB !== '') return -1;
    if (deptA !== '' && deptB === '') return 1;
    if (deptA !== deptB) {
      return deptA.localeCompare(deptB, 'ja');
    }
    
    // 2. 同じ所属内でdisplayOrderでソート：undefinedは下位、定義されているものは昇順
    if (a.displayOrder === undefined && b.displayOrder === undefined) {
      // 両方undefinedの場合は更新日時の新しい順
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
    if (a.displayOrder === undefined) return 1;
    if (b.displayOrder === undefined) return -1;
    
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    
    // 3. displayOrderが同じ場合は更新日時の新しい順
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  // シナリオリストが取得されたら、各シナリオに対してユーザーの実施回数を取得
  // すでにリクエストを送信したシナリオに対しては再度リクエストを送信しないようにする
  useEffect(() => {
    if (displayedScenarios.length > 0 && username) {
      displayedScenarios.forEach((scenario: Scenario) => {
        // すでにリクエストを送信したシナリオはスキップ
        if (!fetchedScenariosRef.current.has(scenario.scenarioId)) {
          fetchedScenariosRef.current.add(scenario.scenarioId);
          fetchScenarioCount(scenario.scenarioId);
        }
      });
    }
  }, [displayedScenarios, username, fetchScenarioCount]);

  // 当月のログイン数を取得するクエリ
  const { loading: loadingThisMonth, error: errorThisMonth, data: thisMonthData } = useQuery(COUNT_EVALUATIONS_BY_MONTH, {
    variables: {
      userId: username,
    },
    skip: !username,
    fetchPolicy: 'network-only'
  });
  const thisMonthCount = thisMonthData?.countEvaluationsByMonth || 0;
  
  // 企業のログイン履歴平均を取得するクエリ
  const { loading: loadingCompany, error: errorCompany, data: companyData } = useQuery(GET_COMPANY, {
    variables: {
      companyId: currentUser?.companyId,
    },
    skip: !currentUser?.companyId,
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error("企業情報取得エラー:", error);
    }
  });
  // エラーがある場合やデータがない場合は0を使用
  const companyUseageCount = errorCompany ? 0 : (companyData?.getCompany?.usageCount ?? 0);

  // ロールプレイ設定ページへ移動
  const handleScenarioStart = (scenarioId: string) => {
    navigate(`/roleplay-settings/${scenarioId}`);
  };

  // さらに表示するボタンのハンドラー
  const handleLoadMore = useCallback(() => {
    if (!nextToken || loadingMore || !currentUser?.companyId) return;
    
    setLoadingMore(true);
    loadMoreScenarios({
      variables: {
        companyId: currentUser.companyId,
        limit: SCENARIOS_PER_PAGE,
        nextToken: nextToken
      }
    });
  }, [nextToken, loadingMore, currentUser?.companyId, loadMoreScenarios]);

  // フィルタリング後の表示件数が不足している場合、自動的に追加データを取得
  useEffect(() => {
    // 初回ローディング中、追加ローディング中、nextTokenがない場合はスキップ
    if (loading || loadingMore || !nextToken) return;
    
    // フィルタリング後の件数がSCENARIOS_PER_PAGE未満の場合、自動的に追加取得
    if (filteredScenarios.length < SCENARIOS_PER_PAGE && currentUser?.companyId) {
      handleLoadMore();
    }
  }, [filteredScenarios.length, loading, loadingMore, nextToken, currentUser?.companyId, handleLoadMore]);

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  return (
    <Box>
      {/* タイトルとタグフィルター */}
      <Grid container spacing={2} sx={{ width: '100%', mb: 3, alignItems: 'center' }}>
        {/* H2エリア */}
        <Grid item xs={12} md={6}>
          <Box className="h2-area">
            <Box className="h2-container" sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={scenariosIcon} 
                alt="推奨ロープレアイコン" 
                style={{ width: '24px', height: '24px', marginRight: '5px' }}
              />
              <Typography 
                variant="h4" 
                component="h2"
                sx={{ 
                  fontSize: '24px',
                  fontWeight: 700,
                  color: theme.palette.smartSkillTalk.linkBlue,
                  wordBreak: 'keep-all',
                  lineHeight: 1.3,
                  '& .break-point': {
                    display: 'none'
                  },
                  '@media (max-width: 500px)': {
                    '& .break-point': {
                      display: 'block',
                      height: 0
                    }
                  }
                }}
              >
                あなたに推奨されている<br className="break-point" />ロープレ一覧
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* タグフィルター */}
        <Grid item xs={12} md={6}>
          <Box>
            <Autocomplete
              multiple
              size="small"
              id="tags-filter"
              options={availableTags}
              value={selectedTags}
              onChange={(event, newValue) => {
                setSelectedTags(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  size="small"
                  placeholder="タグで絞り込む"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <LocalOfferIcon sx={{ color: 'action.active', mr: 1, fontSize: '18px' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option}
                      {...tagProps}
                      size="small"
                      sx={{
                        height: '22px',
                        fontSize: '11px',
                        backgroundColor: theme.palette.smartSkillTalk.green,
                        color: '#FFFFFF',
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: '#FFFFFF'
                          }
                        }
                      }}
                    />
                  );
                })
              }
              sx={{
                '& .MuiInput-root': {
                  '&:before': {
                    borderBottomColor: theme.palette.divider,
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottomColor: theme.palette.primary.main,
                  },
                  '&:after': {
                    borderBottomColor: theme.palette.primary.main,
                  }
                }
              }}
            />
            {selectedTags.length > 0 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 0.5, 
                  display: 'block',
                  color: theme.palette.text.secondary
                }}
              >
                {filteredScenarios.length}件のロープレが見つかりました
              </Typography>
            )}
            {nextToken && (
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 0.5, 
                  display: 'block',
                  color: theme.palette.warning.main
                }}
              >
                ※未読み込みのタグがある可能性があります
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* ローディング表示 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          シナリオの読み込み中にエラーが発生しました。
        </Alert>
      )}

      {/* シナリオ一覧 */}
      <Grid container spacing={3.5} sx={{ width: '100%' }}>
        {sortedScenarios.map((scenario: Scenario) => (
          <Grid item xs={12} sm={6} md={4} key={scenario.scenarioId}>
            <Card
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative',
                backgroundColor: theme.palette.smartSkillTalk.lightBlue,
                border: `1px solid ${theme.palette.primary.light}`,
                borderRadius: '10px',
                padding: '15px',
                boxShadow: 'none',
                cursor: 'pointer'
              }}
              onClick={() => handleScenarioStart(scenario.scenarioId)}
            >
              {/* 実施回数が0の場合に赤い通知ドットを表示 */}
              {(userScenarioCounts[scenario.scenarioId] || 0) === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    boxShadow: '0 0 0 2px white',
                    zIndex: 1
                  }}
                />
              )}
              <CardContent
                sx={{ flexGrow: 1, p: 0 }}
              >
                {/* タイトルと言語バッジ */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="h6" component="div" sx={{ 
                    fontSize: '16px', 
                    fontWeight: 700, 
                    lineHeight: 1.5, 
                    color: theme.palette.smartSkillTalk.linkBlue,
                    flex: 1
                  }}>
                    {scenario.title}
                  </Typography>
                  {scenario.targetLanguage && scenario.targetLanguage !== '日本語' && (
                    <Chip
                      label={scenario.targetLanguage}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '10px',
                        backgroundColor: theme.palette.smartSkillTalk.gray,
                        color: '#FFFFFF',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </Box>
                
                {/* 説明文 */}
                <Typography variant="body2" sx={{ 
                  fontSize: '12px', 
                  fontWeight: 400, 
                  lineHeight: 1.5, 
                  color: theme.palette.smartSkillTalk.linkBlue,
                  mb: 1
                }}>
                  {scenario.description}
                </Typography>
                
                {/* 作成日 */}
                <Box sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: '4px',
                  padding: '1px 13px',
                  alignSelf: 'flex-start',
                  display: 'inline-block',
                  mb: 1
                }}>
                  <Typography variant="caption" sx={{
                    fontSize: '10px',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: theme.palette.smartSkillTalk.darkGray
                  }}>
                    作成日：{formatDate(scenario.createdAt)}
                  </Typography>
                </Box>
                
                {/* タグ表示 */}
                {scenario.tags && scenario.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {scenario.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '10px',
                          backgroundColor: theme.palette.smartSkillTalk.green,
                          color: '#FFFFFF',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}

              </CardContent>
              
              {/* アクションボタン */}
              <CardActions sx={{ justifyContent: 'space-between', p: 0, mt: 2, flexDirection: 'column', alignItems: 'stretch' }}>
                {/* 所属ラベル（設定されている場合のみ表示） */}
                {scenario.department && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    width: '100%',
                    mb: 0.5
                  }}>
                    <Chip
                      label={scenario.department}
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '9px',
                        backgroundColor: theme.palette.smartSkillTalk.linkBlue,
                        color: '#FFFFFF',
                        '& .MuiChip-label': {
                          px: 0.75,
                          py: 0
                        }
                      }}
                    />
                  </Box>
                )}
                
                {/* 実施回数 */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  width: '100%'
                }}>
                  {/* あなたの実施回数 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: '10px', 
                      fontWeight: 400, 
                      lineHeight: 1.2, 
                      letterSpacing: '0.25em',
                      color: theme.palette.smartSkillTalk.linkBlue
                    }}>
                      あなたの<br />実施回数
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontSize: '22px', 
                      fontWeight: 700, 
                      lineHeight: 0.5, 
                      letterSpacing: '0.05em',
                      color: theme.palette.smartSkillTalk.linkBlue,
                      textAlign: 'right',
                      mt: 1
                    }}>
                      {(userScenarioCounts[scenario.scenarioId] || 0).toFixed(0)}
                      <Box component="span" sx={{ 
                        fontSize: '12px', 
                        fontWeight: 400, 
                        letterSpacing: '0.25em'
                      }}>回</Box>
                    </Typography>
                  </Box>

                  {/* みんなの実施回数 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: '10px', 
                      fontWeight: 400, 
                      lineHeight: 1.2, 
                      letterSpacing: '0.25em',
                      color: theme.palette.smartSkillTalk.linkBlue
                    }}>
                      みんなの<br />実施回数
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontSize: '22px', 
                      fontWeight: 700, 
                      lineHeight: 0.5, 
                      letterSpacing: '0.05em',
                      color: theme.palette.smartSkillTalk.linkBlue,
                      textAlign: 'right',
                      mt: 1
                    }}>
                      {(scenario.usageCount || 0).toFixed(1)}
                      <Box component="span" sx={{ 
                        fontSize: '12px', 
                        fontWeight: 400, 
                        letterSpacing: '0.25em'
                      }}>回</Box>
                    </Typography>
                  </Box>
                </Box>
                {/* <Button 
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => handleScenarioDetails(scenario.scenarioId)}
                  sx={{ color: theme.palette.smartSkillTalk.linkBlue }}
                >
                  詳細
                </Button>
                <Button 
                  size="small" 
                  variant="contained" 
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleScenarioStart(scenario.scenarioId)}
                  sx={{ 
                    backgroundColor: theme.palette.primary.light,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    }
                  }}
                >
                  開始
                </Button> */}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* データがない場合のメッセージ */}
      {!loading && sortedScenarios.length === 0 && (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="text.secondary">
            シナリオがありません。
          </Typography>
        </Box>
      )}

      {/* さらに表示するボタン */}
      {nextToken && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 4 }}>
          <Button 
            variant="contained" 
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ 
              backgroundColor: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
              borderRadius: '4px',
              px: 2.5
            }}
          >
            {loadingMore ? '読み込み中...' : 'さらに表示する'}
          </Button>
        </Box>
      )}

      {/* 統計パネル */}
      <Box sx={{ 
        display: 'flex', 
        width: '100%', 
        justifyContent: 'space-between',
        gap: { xs: 2, md: 4.5 },
        flexDirection: { xs: 'column', md: 'row' },
        mt: 2
      }}>
        {/* 今月のログイン回数 */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            p: 2, 
            borderRadius: 1.25,
            backgroundColor: theme.palette.secondary.main,
            color: '#FFFFFF'
          }}
        >
          <Typography variant="h6" sx={{ 
            fontSize: '18px', 
            fontWeight: 400, 
            lineHeight: 1.1,
            flex: 1
          }}>
            今月の<br />ログイン回数
          </Typography>
          {loadingLogs ? (
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
          ) : errorLogs ? (
            <Typography variant="body1">
              エラー
            </Typography>
          ) : (
            <Typography variant="h4" component="div" sx={{ 
              fontSize: '32px', 
              fontWeight: 700, 
              lineHeight: 0.375, 
              letterSpacing: '0.05em',
              textAlign: 'right'
            }}>
              {monthlyLoginCount.toFixed(0)}
              <Box component="span" sx={{ 
                fontSize: '14px', 
                fontWeight: 700, 
                letterSpacing: '0.25em'
              }}>回</Box>
            </Typography>
          )}
        </Paper>

        {/* 今月のロープレ実施回数 */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            p: 2, 
            borderRadius: 1.25,
            backgroundColor: theme.palette.smartSkillTalk.green,
            color: '#FFFFFF'
          }}
        >
          <Typography variant="h6" sx={{ 
            fontSize: '18px', 
            fontWeight: 400, 
            lineHeight: 1.1,
            flex: 1
          }}>
            今月の<br />ロープレ実施回数
          </Typography>
          <Typography variant="h4" component="div" sx={{ 
            fontSize: '32px', 
            fontWeight: 700, 
            lineHeight: 0.375, 
            letterSpacing: '0.05em',
            textAlign: 'right'
          }}>
            {thisMonthCount.toFixed(0)}
            <Box component="span" sx={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              letterSpacing: '0.25em'
            }}>回</Box>
          </Typography>
        </Paper>

        {/* みんなの実施回数（平均） */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            p: 2, 
            borderRadius: 1.25,
            backgroundColor: theme.palette.smartSkillTalk.linkBlue,
            color: '#FFFFFF'
          }}
        >
          <Typography variant="h6" sx={{ 
            fontSize: '18px', 
            fontWeight: 400, 
            lineHeight: 1.1,
            flex: 1
          }}>
            みんなの<br />実施回数（平均）
          </Typography>
          <Typography variant="h4" component="div" sx={{ 
            fontSize: '32px', 
            fontWeight: 700, 
            lineHeight: 0.375, 
            letterSpacing: '0.05em',
            textAlign: 'right'
          }}>
            {companyUseageCount.toFixed(1)}
            <Box component="span" sx={{ 
              fontSize: '14px', 
              fontWeight: 700, 
              letterSpacing: '0.25em'
            }}>回</Box>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default ScenarioListPage;
